import React, { useMemo, useEffect, useState, Suspense } from 'react';
import loadable from '@loadable/component';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import theme2 from './theme/theme';
import ProtectedRoute from './components/ProtectedRoute';
import store from './store/store';

const LoginPage = loadable(() => import('./login/page/LoginPage'));
const DashboardPage = loadable(() => import('./dashboard/page/DashboardPage'));
const ProfilePage = loadable(() => import('./profile/page/ProfilePage'));
const LeaveDiaryPage = loadable(
  () => import('./leavediary/page/LeaveDiaryPage'),
);
const LeaveDiaryListPage = loadable(
  () => import('./leavediary/page/LeaveDiaryListPage'),
);
const ChatRoomPage = loadable(() => import('./chat/page/ChatRoomPage'));
const WebSocketTestPage = loadable(() => import('./WebSocketTest'));
const ChatPage = loadable(() => import('./chat/page/ChatPage'));
const UserList = loadable(() => import('./userlist/page/UserListPage'));
const UserDetailPage = loadable(() => import('./userlist/page/UserDetailPage'));
const UserFollowPage = loadable(() => import('./follow/page/UserFollowPage'));
const FollowPage = loadable(() => import('./follow/page/FollowPage'));
const SingleChatRoomPage = loadable(
  () => import('./chat/page/SingleChatRoomPage'),
);
const HideChatRoomPage = loadable(() => import('./chat/page/HideChatRoomPage'));
const HideUserListPage = loadable(
  () => import('./userlist/page/HideUserListPage'),
);
const OAuthLoginPage = loadable(() => import('./login/page/OAuthLoginPage'));
const KakaoLoginPage = loadable(() => import('./login/page/KakaoLoginPage'));
const NaverLoginPage = loadable(() => import('./login/page/NaverLoginPage'));
const SettigPage = loadable(() => import('./setting/page/SettingPage'));
const ReportPage = loadable(() => import('./report/page/ReportPage'));

declare global {
  interface Window {
    tempProperty: string;
  }
}

const AppContent: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    const state = store.getState();
    const token = state.login.token;
    //const token = localStorage.getItem('token');
    if (token) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  }, [location]);

  return (
    <ThemeProvider theme={theme2}>
      <CssBaseline />
      <Routes>
        <Route
          path='/login'
          element={
            !isLoggedIn ? <LoginPage /> : <Navigate to='/dashboard' replace />
          }
        />
        <Route
          path='/oauthlogin'
          element={
            !isLoggedIn ? (
              <OAuthLoginPage />
            ) : (
              <Navigate to='/dashboard' replace />
            )
          }
        />
        <Route
          path='/kakao/callback'
          element={
            !isLoggedIn ? (
              <KakaoLoginPage />
            ) : (
              <Navigate to='/dashboard' replace />
            )
          }
        />
        <Route
          path='/naver/callback'
          element={
            !isLoggedIn ? (
              <NaverLoginPage />
            ) : (
              <Navigate to='/dashboard' replace />
            )
          }
        />
        <Route
          path='/dashboard'
          element={<ProtectedRoute element={<DashboardPage />} />}
        />
        <Route
          path='/profile'
          element={<ProtectedRoute element={<ProfilePage />} />}
        />
        <Route
          path='/leavediary'
          element={<ProtectedRoute element={<LeaveDiaryPage />} />}
        />
        <Route
          path='/diary'
          element={<ProtectedRoute element={<LeaveDiaryListPage />} />}
        />
        <Route
          path='/chatroom'
          element={<ProtectedRoute element={<ChatRoomPage />} />}
        />
        <Route
          path='/chat'
          element={<ProtectedRoute element={<ChatPage />} />}
        />
        <Route
          path='/websocket'
          element={<ProtectedRoute element={<WebSocketTestPage />} />}
        />
        <Route
          path='/userlist'
          element={<ProtectedRoute element={<UserList />} />}
        />
        <Route
          path='/user/:userId'
          element={<ProtectedRoute element={<UserDetailPage />} />}
        />
        <Route
          path={`/user/:userId/follow`}
          element={<ProtectedRoute element={<UserFollowPage />} />}
        />
        <Route
          path='/follow'
          element={<ProtectedRoute element={<FollowPage />} />}
        />
        <Route
          path='/singlechat'
          element={<ProtectedRoute element={<SingleChatRoomPage />} />}
        />
        <Route
          path='/hidechatroom'
          element={<ProtectedRoute element={<HideChatRoomPage />} />}
        />
        <Route
          path='/hideuser'
          element={<ProtectedRoute element={<HideUserListPage />} />}
        />
        <Route
          path='/setting'
          element={<ProtectedRoute element={<SettigPage />} />}
        />
        <Route
          path='/report'
          element={<ProtectedRoute element={<ReportPage />} />}
        />
        <Route
          path='*'
          element={
            isLoggedIn ? (
              <Navigate to='/dashboard' replace />
            ) : (
              <Navigate to='/login' replace />
            )
          }
        />
      </Routes>
    </ThemeProvider>
  );
};

const App: React.FC = () => (
  <BrowserRouter basename={process.env.REACT_APP_BASE_URL}>
    <AppContent />
  </BrowserRouter>
);

export default App;
